// src/pages/Instrument_Runs/components/InstrumentRunHeader/InstrumentRunHeader.tsx
import { useDateFormatter } from '@/hooks';
import { useAssayRunConfigs, useAssays } from '@/hooks/useAssays';
import {
  findRunConfigByAssayId,
  sampleCountOptionsFor,
} from '@/pages/Instrument_Runs/components/RunEditForm/assaySelection';
import { SPECIES_LABELS, type Species, speciesLabel } from '@/types';
import { isSixDigitCode } from '@/types/utils/sixDigitCode';
import { Code } from '@components/index';
import { Grid } from '@nautilusbio/nb-design-system/Grid';
import { Input } from '@nautilusbio/nb-design-system/Input';
import { Select } from '@nautilusbio/nb-design-system/Select';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { Textarea } from '@nautilusbio/nb-design-system/Textarea';
import { Tooltip } from '@nautilusbio/nb-design-system/Tooltip';
import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';

import styles from '../../styles.module.css';
import type { InstrumentRunForm, InstrumentRunPublic, SixDigitCode } from '../../types';
import RunStatusGrid from '../RunStatusGrid';

interface Props {
  run: InstrumentRunPublic;
  isEditing?: boolean;
  form?: InstrumentRunForm;
}

const hoverOpts = { delay: 2000 };

// Species values are lower_snake on the wire and index-aligned with their display labels,
// which come from the shared SPECIES_LABELS map rather than a local duplicate.
const SPECIES_OPTION_VALUES: readonly Species[] = ['human', 'mouse'] as const;
const SPECIES_OPTION_LABELS = SPECIES_OPTION_VALUES.map(value => SPECIES_LABELS[value]);

type SampleCountFieldProps = { Field: NonNullable<InstrumentRunForm['Field']>; label: string };

/** Options come from the selected assay's run config — the backend rejects a count it doesn't list;
 *  until the catalog resolves, the wider SAMPLE_COUNT_FALLBACK shows. The outer `Field name="assay_id"`
 *  only subscribes: this header is memoized on a stable `form`, so a switch wouldn't recompute. */
const SampleCountField = ({ Field, label }: SampleCountFieldProps) => {
  const { data: assayRunConfigs = [], isLoading } = useAssayRunConfigs();

  return (
    <Field name="assay_id">
      {assayField => {
        const options = sampleCountOptionsFor(
          findRunConfigByAssayId(assayRunConfigs, assayField.state.value as string | undefined),
        );

        return (
          <Field name="noOfSamples">
            {field => (
              <Select
                allowClear={false}
                data-testid="select-no-of-samples"
                id="no-of-samples"
                isPending={isLoading}
                label={{ children: label, id: 'label-no-of-samples' }}
                onChange={idx => {
                  const val = options[idx];
                  if (val !== undefined) field.handleChange(val);
                }}
                options={options}
                value={options.indexOf((field.state.value as string) ?? '')}
              />
            )}
          </Field>
        );
      }}
    </Field>
  );
};

type MetaFieldProps = { label: string; value: string };

const MetaField = ({ label, value }: MetaFieldProps) => (
  <Stack gap={1} orientation="vertical">
    <Text as="span" className={styles.headerMetaLabel} size="xs">
      {label}
    </Text>

    <Text size="sm">{value}</Text>
  </Stack>
);

const InstrumentRunHeader = ({ run, isEditing = false, form }: Props) => {
  const { t } = useTranslation('runs', { keyPrefix: 'header' });
  const formatDate = useDateFormatter();
  // Assay is an FK now, so the picker options come from the catalog rather than an enum.
  const { data: assays = [], isLoading: isAssaysLoading } = useAssays();

  const showPin = run.status === 'submitted' && isSixDigitCode(run?.code ?? '');
  const Field = form?.Field;
  const canEdit = isEditing && Field;

  // When editing or a description is present, the meta grid lives below the body row.
  // When neither, it lifts into the description slot to eliminate the blank left column.
  const hasDescriptionSlot = canEdit || !!run.description;

  const metaGrid = (
    <Grid className={styles.headerMetaGrid} gridTemplateColumns="repeat(3, 1fr)">
      {/* Row 1 */}
      {canEdit ? (
        <SampleCountField Field={Field} label={t('samples')} />
      ) : (
        <MetaField label={t('samples')} value={typeof run.num_samples === 'number' ? String(run.num_samples) : '—'} />
      )}
      {canEdit ? (
        <Field name="species">
          {field => (
            <Select
              allowClear={false}
              data-testid="select-species"
              id="species"
              label={{ children: t('species'), id: 'label-species' }}
              onChange={idx => {
                const val = SPECIES_OPTION_VALUES[idx];
                if (val !== undefined) field.handleChange(val);
              }}
              options={[...SPECIES_OPTION_LABELS]}
              value={SPECIES_OPTION_VALUES.indexOf(field.state.value as Species)}
            />
          )}
        </Field>
      ) : (
        <MetaField label={t('species')} value={run.species ? speciesLabel(run.species) : '—'} />
      )}
      {showPin ? (
        <Stack alignItems="center" gap={2}>
          <Text as="span" className={styles.headerMetaLabel}>
            {t('pin_to_start_run')}:
          </Text>

          <Code code={run.code as SixDigitCode} id={`${run.id}-code`} />
        </Stack>
      ) : (
        <MetaField label={t('instrument')} value={run.instrument_name ?? '—'} />
      )}

      {/* Row 2 */}
      <MetaField label={t('modified')} value={run.updated_at ? formatDate(run.updated_at) : '—'} />
      <MetaField label={t('started')} value={run.started_at ? formatDate(run.started_at) : '—'} />
      <MetaField label={t('submitted_by')} value={run.submitted_by_full_name ?? '—'} />

      {/* Row 3 */}
      <MetaField label={t('created')} value={formatDate(run.created_at)} />
      <MetaField label={t('finished')} value={run.completed_at ? formatDate(run.completed_at) : '—'} />
      <MetaField label={t('submitted')} value={run.submitted_at ? formatDate(run.submitted_at) : '—'} />
    </Grid>
  );

  return (
    <Stack className={styles.headerCard} fullWidth orientation="vertical">
      {/* Title row: run name + assay version */}
      <Grid alignItems="center" className={styles.headerTitleRow} fullWidth gap={6} gridTemplateColumns="1fr .2fr">
        {canEdit ? (
          <Field name="name">
            {field => (
              <Input
                data-testid="run-name-input"
                fullWidth
                label={{
                  children: t('name'),
                  htmlFor: 'run-name',
                  id: 'label-run-name',
                }}
                onChange={e => field.handleChange((e.target as HTMLInputElement).value)}
                value={(field.state.value as string) ?? ''}
              />
            )}
          </Field>
        ) : (
          <Tooltip
            content={t('id_label', { id: run.id })}
            hoverOptions={hoverOpts}
            placement="right-start"
            trigger={<Text as="h1">{run.name}</Text>}
          />
        )}

        {canEdit ? (
          <Field name="assay_id">
            {field => (
              <Select
                allowClear={false}
                classNames={{
                  container: styles.assaySelect,
                }}
                data-testid="select-assay-id"
                id="assay-id"
                isPending={isAssaysLoading}
                label={{
                  children: t('assay'),
                  id: 'label-assay-id',
                }}
                onChange={idx => {
                  const selected = assays[idx];
                  if (selected !== undefined) field.handleChange(selected.id);
                }}
                options={assays.map(a => a.assay_friendly_name)}
                value={assays.findIndex(a => a.id === field.state.value)}
              />
            )}
          </Field>
        ) : (
          <Text className={styles.assaySelect} size="lg">
            {run.assay_friendly_name}
          </Text>
        )}
      </Grid>

      {/* Body row: description or inline meta (left) + status grid (right) */}
      <Grid alignItems="start" className={styles.headerBodyRow} gap={6} gridTemplateColumns="3fr 1fr">
        {canEdit ? (
          <Field name="description">
            {field => (
              <Textarea
                data-testid="run-description-textarea"
                label={{
                  children: t('description'),
                  id: 'label-run-description',
                  htmlFor: 'run-description',
                }}
                onChange={e => field.handleChange((e.target as HTMLTextAreaElement).value)}
                value={(field.state.value as string) ?? ''}
              />
            )}
          </Field>
        ) : run.description ? (
          <Text as="small" className={styles.headerDescription} size="sm">
            {run.description}
          </Text>
        ) : (
          metaGrid
        )}

        <Stack className={styles.headerStatusColumn} gap={3} orientation="vertical">
          <RunStatusGrid run={run} />
          {run.status === 'completed' && !isEditing && (
            <Stack className={styles.completionMessage} gap={1} orientation="vertical">
              <Text size="sm">{t('run_complete')}</Text>
              {/* TODO: show "X of N samples passed all QCs" when sample QC count
                  fields land on SamplePublic — no API backing yet */}
            </Stack>
          )}
        </Stack>
      </Grid>

      {/* Metadata grid below body row: only when editing or a description is present */}
      {hasDescriptionSlot && metaGrid}
    </Stack>
  );
};

export default memo(InstrumentRunHeader);
